import { FC, ReactNode } from 'react';
import { useTranslation } from 'react-i18next';

import { InputAdornment, TextField as MuiTextField } from '@mui/material';
import clsx from 'clsx';
import { useBusinessToolStore } from 'store/useBusinessToolStore';
import { getBusinessPlanCurrency } from 'store/useBusinessToolStore/selectors';

import { ReactComponent as PcsIcon } from 'assets/icons/PcsIcon.svg';
import { ReactComponent as PercentageIcon } from 'assets/icons/PercentageIcon.svg';

import { FormField } from 'components/_basic/Form/FormField';

import { getCurrencySymbol } from 'helpers/getCurrencySymbol';
import noop from 'helpers/noop';

import { IOnAdornmentClick, ITextFieldProps } from './types';

import styles from './TextInput.module.scss';

export const TextInput: FC<ITextFieldProps> = ({
  id,
  label,
  value,
  fieldError,
  fieldClassName,
  multiline,
  maxLength = 1500,
  adornmentType,
  hiddenLabel = false,
  placeholder = '',
  endAdornmentIcon: propEndAdornmentIcon,
  startAdornmentIcon: propStartAdornmentIcon,
  status,
  requiredForAI = false,
  onEndAdornmentClick = noop,
  onStartAdornmentClick = noop,
  readOnly = false,
  ...restProps
}) => {
  const { t } = useTranslation();
  const stringValue = String(value);
  const characters = multiline ? stringValue.length : 0;

  const currency = useBusinessToolStore(getBusinessPlanCurrency);

  const currencySymbol = getCurrencySymbol(currency);

  const defaultAdornmentValues: { [K in NonNullable<ITextFieldProps['adornmentType']>]: ReactNode } = {
    currency: currencySymbol,
    percentage: <PercentageIcon />,
    pcs: <PcsIcon />,
  };

  const getAdornmentValue = (adornmentVariant?: 'currency' | 'percentage' | 'pcs') =>
    propEndAdornmentIcon || (adornmentVariant && defaultAdornmentValues[adornmentVariant]);

  const createAdornment = (position: 'start' | 'end', icon: ReactNode, onImageAdornmentClick: IOnAdornmentClick) =>
    icon && (
      <InputAdornment
        position={position}
        onClick={onImageAdornmentClick}
        className={clsx(styles.inputAdornment, {
          [styles.disabledAdornment]: restProps.disabled,
          [styles.endAdornmentBorder]: position === 'end',
          [styles.interactive]: !!onImageAdornmentClick,
        })}
      >
        {icon}
      </InputAdornment>
    );

  const endAdornment = createAdornment('end', getAdornmentValue(adornmentType), onEndAdornmentClick);
  const startAdornment = createAdornment('start', propStartAdornmentIcon, onStartAdornmentClick);

  return (
    <FormField
      id={id}
      name={restProps.name}
      label={label}
      error={fieldError?.message}
      disabled={restProps.disabled}
      className={fieldClassName}
      requiredForAI={requiredForAI}
    >
      <MuiTextField
        fullWidth
        {...restProps}
        value={value}
        placeholder={multiline ? t('textAreaPlaceholder') : placeholder ? placeholder : ''}
        className={clsx(
          styles.input,
          adornmentType === 'pcs' && styles.pcsAdditionalPadding,
          status === 'error' && styles.inputError,
          status === 'success' && styles.inputSuccess,
          restProps.isPreventInactiveScroll && styles.preventInactiveScroll,
          restProps.className,
        )}
        multiline={multiline}
        inputProps={{
          maxLength: maxLength,
          ...restProps.inputProps,
          'data-mirror-field': restProps['data-mirror-field'],
        }}
        helperText={
          multiline && maxLength && !hiddenLabel
            ? `${maxLength - characters}/${maxLength} ${t('characters')}`
            : restProps.helperText
        }
        error={true}
        FormHelperTextProps={{ classes: { root: styles.helperText } }}
        InputProps={{
          endAdornment,
          startAdornment,
          readOnly,
          ...restProps.InputProps,
        }}
      />
    </FormField>
  );
};
